import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus,
  Trash,
  VideoCamera,
  ArrowLeft,
  FloppyDisk,
  FileArrowDown,
  CursorClick,
  ArrowSquareOut,
} from "@phosphor-icons/react";
import { api, brl, errMsg } from "@/lib/api";
import { DirectBuyStepCard } from "@/components/videobuy/DirectBuyStepCard";

const emptyForm = {
  title: "",
  video_url: "",
  cta_text: "Continuar",
  require_watch_percent: 95,
  affiliate_rate: 0.10,
  direct_buy_enabled: false,
  direct_buy_video_url: "",
  direct_buy_headline: "",
  direct_buy_button_text: "Comprar agora",
  direct_buy_require_watch: false,
  direct_buy_accept_card: true,
  redirect_url: "",
};
const DIRECT_KEYS = ["direct_buy_enabled", "direct_buy_video_url", "direct_buy_headline", "direct_buy_button_text", "direct_buy_require_watch", "direct_buy_accept_card", "redirect_url"];

export default function SalesLinkEditor() {
  const nav = useNavigate();
  const { id } = useParams();
  const editing = !!id;
  const [form, setForm] = useState(emptyForm);
  const [plans, setPlans] = useState([{ name: "Plano Básico", price: 47, description: "" }]);
  const [videos, setVideos] = useState([]);
  const [files, setFiles] = useState([]);
  const [buttons, setButtons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingInit, setLoadingInit] = useState(editing);

  const inp = "w-full bg-[var(--bg-base)] border border-[var(--border)] rounded-sm px-4 py-3 focus:border-[var(--accent)] outline-none";

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const { data } = await api.get(`/funnels/${id}`);
        setForm({
          title: data.title || "",
          video_url: data.video_url || "",
          file_url: data.file_url || "",
          file_description: data.file_description || "",
          cta_text: data.cta_text || "Continuar",
          require_watch_percent: data.require_watch_percent ?? 95,
          affiliate_rate: data.affiliate_rate ?? 0.10,
          ...Object.fromEntries(DIRECT_KEYS.map((k) => [k, data[k] ?? emptyForm[k]])),
        });
        setPlans(data.plans?.length ? data.plans : [{ name: "Plano Básico", price: 47, description: "" }]);
        setVideos(data.delivery_videos || []);
        setFiles(data.files || []);
        setButtons(data.buttons || []);
      } catch (e) {
        toast.error(errMsg(e, "Erro ao carregar funil"));
        nav("/app/funnels");
      } finally { setLoadingInit(false); }
    })();
  }, [id, editing, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.direct_buy_enabled && !/^https?:\/\/\S+$/i.test((form.redirect_url || "").trim())) {
      toast.error("Passo 6: informe o link de redirecionamento (https://...)");
      return;
    }
    setLoading(true);
    try {
      const cleanFiles = files.filter((f) => f.url && f.url.trim());
      const cleanButtons = buttons
        .filter((b) => b.url && b.url.trim())
        .map((b) => ({
          label: (b.label || "").trim() || "Abrir",
          url: b.url.trim(),
          style: b.style || "primary",
        }));
      const legacy = cleanFiles[0] || {};
      const payload = {
        ...form,
        plans,
        delivery_videos: videos,
        files: cleanFiles,
        buttons: cleanButtons,
        file_url: legacy.url || null,
        file_description: legacy.description || "",
      };
      if (editing) {
        await api.put(`/funnels/${id}`, payload);
        toast.success("Funil atualizado!");
      } else {
        await api.post("/funnels", payload);
        toast.success("Funil criado!");
      }
      nav("/app/funnels");
    } catch (err) {
      toast.error(errMsg(err, "Erro"));
    } finally { setLoading(false); }
  };

  const addPlan = () => setPlans([...plans, { name: "", price: 0, description: "" }]);
  const rmPlan = (i) => setPlans(plans.filter((_, x) => x !== i));
  const updPlan = (i, k, v) => setPlans(plans.map((p, x) => x === i ? { ...p, [k]: k === "price" ? Number(v) : v } : p));

  const addVid = () => setVideos([...videos, { name: "", url: "" }]);
  const rmVid = (i) => setVideos(videos.filter((_, x) => x !== i));
  const updVid = (i, k, v) => setVideos(videos.map((it, x) => x === i ? { ...it, [k]: v } : it));

  const addFile = () => setFiles([...files, { name: "", description: "", url: "" }]);
  const rmFile = (i) => setFiles(files.filter((_, x) => x !== i));
  const updFile = (i, k, v) => setFiles(files.map((it, x) => x === i ? { ...it, [k]: v } : it));

  const addButton = () => setButtons([...buttons, { label: "", url: "", style: "primary" }]);
  const rmButton = (i) => setButtons(buttons.filter((_, x) => x !== i));
  const updButton = (i, k, v) => setButtons(buttons.map((it, x) => x === i ? { ...it, [k]: v } : it));

  if (loadingInit) {
    return (
      <div className="fadeUp max-w-4xl">
        <div className="card-flat p-10 text-center text-white/50" data-testid="funnel-loading">
          Carregando funil...
        </div>
      </div>
    );
  }

  return (
    <div className="fadeUp max-w-4xl">
      <button onClick={() => nav("/app/funnels")}
        className="btn-ghost text-xs flex items-center gap-1 mb-4" data-testid="back-to-funnels">
        <ArrowLeft size={14} /> Voltar
      </button>
      <div className="label-caps mb-2">Construtor</div>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-10">
        {editing ? "Editar funil" : "Novo link de vendas"}
      </h1>

      <form onSubmit={submit} className="space-y-6">
        {/* PASSO 1 · VÍDEO DE APRESENTAÇÃO */}
        <div className="card-flat p-6 space-y-4">
          <div className="label-caps">Passo 1 · Vídeo de apresentação (protegido)</div>
          <div>
            <label className="label-caps block mb-2">Título do funil</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="funnel-title" className={inp} placeholder="Ex: Curso Delta Vendas" />
          </div>
          <div>
            <label className="label-caps block mb-2">URL do vídeo (YouTube, Vimeo ou MP4 direto)</label>
            <input required value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} data-testid="funnel-video" className={inp} placeholder="https://youtube.com/watch?v=..." />
            <div className="text-xs text-white/50 mt-2">Vídeo com proteção anti-download (menu direito bloqueado, sem picture-in-picture, sem botão de download).</div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label-caps block mb-2">Texto do botão &quot;Próximo&quot;</label>
              <input value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} data-testid="funnel-cta" className={inp} />
            </div>
            <div>
              <label className="label-caps block mb-2">Só liberar botão após ver (%)</label>
              <input type="number" min="0" max="100" value={form.require_watch_percent} onChange={(e) => setForm({ ...form, require_watch_percent: Number(e.target.value) })} data-testid="funnel-watch-pct" className={inp} />
            </div>
            <div>
              <label className="label-caps block mb-2">Comissão de afiliado (%)</label>
              <input type="number" min="0" max="90" step="1" value={form.affiliate_rate * 100} onChange={(e) => setForm({ ...form, affiliate_rate: Number(e.target.value) / 100 })} data-testid="funnel-aff-rate" className={inp} />
              <div className="text-xs text-white/50 mt-1">Quanto quem indicar via <code>?ref=</code> vai ganhar por venda. Plataforma retém sua comissão configurada.</div>
            </div>
          </div>
        </div>

        {/* PASSO 2 · Vídeos da área de acesso (fica em cima) */}
        <div className="card-flat p-6 space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <div className="label-caps flex items-center gap-1"><VideoCamera size={12} /> Passo 2 · Vídeos da área de acesso</div>
              <div className="text-xs text-white/50 mt-1">Aparecem no <b>topo</b> da área do aluno após o pagamento.</div>
            </div>
            <button type="button" onClick={addVid} className="btn-ghost text-xs flex items-center gap-1" data-testid="add-video"><Plus size={14} /> Adicionar vídeo</button>
          </div>
          {videos.length === 0 && <div className="text-white/40 text-sm">Nenhum vídeo adicionado ainda.</div>}
          {videos.map((v, i) => (
            <div key={`vid-${i}`} className="border border-[var(--border)] rounded-sm p-4 grid md:grid-cols-[1fr_2fr_auto] gap-3">
              <input placeholder="Nome do vídeo (ex: Aula 1)" value={v.name} onChange={(e) => updVid(i, "name", e.target.value)} data-testid={`video-name-${i}`} className={inp} />
              <input placeholder="URL (YouTube, Vimeo ou MP4)" value={v.url} onChange={(e) => updVid(i, "url", e.target.value)} data-testid={`video-url-${i}`} className={inp} />
              <button type="button" onClick={() => rmVid(i)} className="btn-ghost px-3 text-[var(--danger)]" data-testid={`rm-video-${i}`}><Trash size={14} /></button>
            </div>
          ))}
        </div>

        {/* PASSO 3 · Arquivos para download (abaixo do vídeo) */}
        <div className="card-flat p-6 space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <div className="label-caps flex items-center gap-1"><FileArrowDown size={12} /> Passo 3 · Arquivos para download</div>
              <div className="text-xs text-white/50 mt-1">Aparecem <b>abaixo</b> do vídeo. PDFs, planilhas, links do Drive, etc.</div>
            </div>
            <button type="button" onClick={addFile} className="btn-ghost text-xs flex items-center gap-1" data-testid="add-file"><Plus size={14} /> Adicionar arquivo</button>
          </div>
          {files.length === 0 && <div className="text-white/40 text-sm">Nenhum arquivo adicionado ainda.</div>}
          {files.map((f, i) => (
            <div key={`file-${i}`} className="border border-[var(--border)] rounded-sm p-4 space-y-3">
              <div className="grid md:grid-cols-[1fr_2fr_auto] gap-3">
                <input placeholder="Nome (ex: Apostila PDF)" value={f.name} onChange={(e) => updFile(i, "name", e.target.value)} data-testid={`file-name-${i}`} className={inp} />
                <input placeholder="URL do arquivo (Drive, Dropbox, S3, etc.)" value={f.url} onChange={(e) => updFile(i, "url", e.target.value)} data-testid={`file-url-${i}`} className={inp} />
                <button type="button" onClick={() => rmFile(i)} className="btn-ghost px-3 text-[var(--danger)]" data-testid={`rm-file-${i}`}><Trash size={14} /></button>
              </div>
              <input placeholder="Descrição curta (opcional)" value={f.description} onChange={(e) => updFile(i, "description", e.target.value)} data-testid={`file-desc-${i}`} className={inp} />
            </div>
          ))}
        </div>

        {/* PASSO 4 · Botões de link (novo) */}
        <div className="card-flat p-6 space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <div className="label-caps flex items-center gap-1"><CursorClick size={12} /> Passo 4 · Botões de link</div>
              <div className="text-xs text-white/50 mt-1">Aparecem <b>abaixo do vídeo</b> junto com os arquivos. Use para abrir uma página externa, grupo do WhatsApp/Telegram, checkout de bônus, etc.</div>
            </div>
            <button type="button" onClick={addButton} className="btn-ghost text-xs flex items-center gap-1" data-testid="add-button"><Plus size={14} /> Adicionar botão</button>
          </div>
          {buttons.length === 0 && <div className="text-white/40 text-sm">Nenhum botão adicionado ainda.</div>}
          {buttons.map((b, i) => (
            <div key={`btn-${i}`} className="border border-[var(--border)] rounded-sm p-4 space-y-3">
              <div className="grid md:grid-cols-[1fr_2fr_auto_auto] gap-3">
                <input
                  placeholder="Texto (ex: Entrar no grupo)"
                  value={b.label}
                  onChange={(e) => updButton(i, "label", e.target.value)}
                  data-testid={`button-label-${i}`}
                  className={inp}
                />
                <input
                  placeholder="URL de destino (https://...)"
                  value={b.url}
                  onChange={(e) => updButton(i, "url", e.target.value)}
                  data-testid={`button-url-${i}`}
                  className={inp}
                />
                <select
                  value={b.style || "primary"}
                  onChange={(e) => updButton(i, "style", e.target.value)}
                  data-testid={`button-style-${i}`}
                  className={inp + " !min-w-[130px]"}
                >
                  <option value="primary">Estilo cheio</option>
                  <option value="outline">Estilo contorno</option>
                </select>
                <button
                  type="button"
                  onClick={() => rmButton(i)}
                  className="btn-ghost px-3 text-[var(--danger)]"
                  data-testid={`rm-button-${i}`}
                >
                  <Trash size={14} />
                </button>
              </div>
              {/* Preview */}
              {b.url && (
                <div className="flex items-center gap-2 text-xs text-white/50">
                  <span>Preview:</span>
                  <span
                    className={
                      (b.style === "outline"
                        ? "border border-[var(--accent)] text-[var(--accent)] "
                        : "bg-[var(--accent)] text-black ") +
                      "inline-flex items-center gap-1 px-3 py-1.5 rounded-sm text-xs font-bold"
                    }
                  >
                    <ArrowSquareOut size={12} weight="bold" /> {b.label || "Abrir"}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* PASSO 5 · Planos */}
        <div className="card-flat p-6 space-y-4">
          <div className="flex justify-between items-center">
            <div className="label-caps">Passo 5 · Planos de venda</div>
            <button type="button" onClick={addPlan} className="btn-ghost text-xs flex items-center gap-1" data-testid="add-plan"><Plus size={14} /> Adicionar plano</button>
          </div>
          {plans.map((p, i) => (
            <div key={`plan-${i}`} className="border border-[var(--border)] rounded-sm p-4 grid md:grid-cols-3 gap-3">
              <input placeholder="Nome do plano" value={p.name} onChange={(e) => updPlan(i, "name", e.target.value)} data-testid={`plan-name-${i}`} className={inp} />
              <input type="number" step="0.01" placeholder="Preço" value={p.price} onChange={(e) => updPlan(i, "price", e.target.value)} data-testid={`plan-price-${i}`} className={inp} />
              <div className="flex gap-2">
                <input placeholder="Descrição" value={p.description} onChange={(e) => updPlan(i, "description", e.target.value)} className={inp} />
                {plans.length > 1 && <button type="button" onClick={() => rmPlan(i)} className="btn-ghost px-3 text-[var(--danger)]"><Trash size={14} /></button>}
              </div>
              <div className="md:col-span-3 text-xs text-white/50">Valor: <b className="text-[var(--accent)]">{brl(p.price)}</b></div>
            </div>
          ))}
        </div>

        {/* PASSO 6 · Vídeo + Compra direta (opcional) */}
        <DirectBuyStepCard form={form} setForm={setForm} watchPct={form.require_watch_percent} />

        <button disabled={loading} data-testid="save-funnel" className="btn-primary w-full flex items-center justify-center gap-2">
          <FloppyDisk size={18} weight="fill" />
          {loading ? "Salvando..." : (editing ? "Salvar alterações" : "Publicar funil")}
        </button>
      </form>
    </div>
  );
}
